/**
 * Gateway Statistics Tab
 * Uses shared CampaignStatsDashboard with gateways-specific data.
 * Same layout as Drop and List statistics.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useMemo } from 'react';

import {
  useAuth,
  useGatewayPerformanceTags,
  useGatewaysWithStats,
  useGatewaysCrossStats,
} from '@/hooks';

import {
  CampaignStatsDashboard,
  TruncatedGatewayCell,
  FILTER_FIELDS_GATEWAYS,
  GROUP_BY_OPTIONS_GATEWAYS,
  crossPairForGroupBy,
  buildCrossStatsGroups,
  toRangeISO,
  toGenericStatsRowFromGateway,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

interface GatewayStatsTabProps {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
}

export const GatewayStatsTab: FC<GatewayStatsTabProps> = ({
  countryKey,
  wiserIds,
  workDate,
}) => {
  const { user } = useAuth();
  const state = useStatsTabState(workDate, 'gateways');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const perfTagsQuery = useGatewayPerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsByGatewayId = perfTagsQuery.data?.tagsByGatewayId ?? {};
  const tagDetailsByGatewayId = perfTagsQuery.data?.tagDetailsByGatewayId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const cross = crossPairForGroupBy(state.groupBy);
  const isCross = cross != null;

  const query = useGatewaysWithStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: !isCross,
  });

  const crossQuery = useGatewaysCrossStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    cross,
  });

  const activeQuery = isCross ? crossQuery : query;

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useGatewaysWithStats({
    countryKey,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: compare.compareMode && !isCross,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    (compareQuery.data?.rows ?? []).forEach((r, i) => {
      const g = toGenericStatsRowFromGateway(r, i);
      map.set(g.id, g.voluum);
    });
    return map;
  }, [compareQuery.data?.rows]);

  const allGatewayRows = query.data?.rows ?? [];
  const singleGenericRows = useMemo(
    () =>
      allGatewayRows.map((r, i) => {
        const base = toGenericStatsRowFromGateway(r, i);
        const gatewayId = r.gatewayId ? String(r.gatewayId).trim() : '';
        const tags =
          gatewayId
            ? (tagsByGatewayId[gatewayId] ??
              Object.entries(tagsByGatewayId).find(
                ([k]) => k.toLowerCase() === gatewayId.toLowerCase(),
              )?.[1]) ?? []
            : [];
        return { ...base, performanceTags: tags };
      }),
    [allGatewayRows, tagsByGatewayId],
  );

  const single = useFilteredAndSortedRowsGeneric(
    singleGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const crossGroups = useMemo(
    () => buildCrossStatsGroups(crossQuery.data, state.sort),
    [crossQuery.data, state.sort],
  );
  const crossFlatRows = useMemo(
    () => crossGroups.flatMap((g) => g.rows),
    [crossGroups],
  );

  const rows = isCross ? crossFlatRows : single.rows;
  const groups = isCross ? crossGroups : single.groups;
  const totals = activeQuery.data?.totals ?? {};
  const allRowsLength = isCross ? crossFlatRows.length : singleGenericRows.length;

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsByGatewayId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsByGatewayId]);

  const tagDetailsLookup = useCallback(
    (gatewayId: string) => {
      if (!gatewayId) return undefined;
      const direct = tagDetailsByGatewayId[gatewayId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByGatewayId).find(
        ([k]) => k.toLowerCase() === gatewayId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByGatewayId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      if (isCross) {
        return (
          <span className="block truncate text-sm text-foreground">
            {row.label || '—'}
          </span>
        );
      }
      const gatewayId = row.id;
      const performanceTags = gatewayId
        ? (tagsByGatewayId[gatewayId] ??
          Object.entries(tagsByGatewayId).find(
            ([k]) => k.toLowerCase() === gatewayId.toLowerCase(),
          )?.[1])
        : undefined;
      const performanceTagDetails = gatewayId ? tagDetailsLookup(gatewayId) : undefined;
      return (
        <TruncatedGatewayCell
          gatewayId={gatewayId}
          label={row.label ?? '—'}
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
        />
      );
    },
    [isCross, tagsByGatewayId, tagDetailsLookup, tagColors],
  );

  return (
    <CampaignStatsDashboard
      tab="gateways"
      title="Gateways"
      emptyMessage="No gateways found for this range."
      loadingTitle="Loading gateway performance data"
      loadingDesc="Fetching tracker metrics grouped by gateway."
      errorPrefix="Failed to load gateway stats."
      firstColumnLabel="Gateway"
      firstColumnSortKey="gateway"
      rowUnitLabel="gateway"
      filterFields={FILTER_FIELDS_GATEWAYS}
      groupByOptions={GROUP_BY_OPTIONS_GATEWAYS}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={activeQuery.data?.total ?? allRowsLength}
      allRowsLength={allRowsLength}
      currency={activeQuery.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={activeQuery.isError ?? false}
      error={activeQuery.error}
      isLoading={activeQuery.isLoading ?? false}
      isFetching={activeQuery.isFetching ?? false}
      dataUpdatedAt={activeQuery.dataUpdatedAt}
      refetch={() => activeQuery.refetch()}
      showLimitSelect
      showGroupBySelect
      tagFilterOptions={availableTags}
      csvExportUserId={user?.id}
      compareCapable={!isCross}
      compareMode={compare.compareMode}
      onToggleCompare={compare.toggleCompareMode}
      compareFromDate={compare.compareFrom}
      compareToDate={compare.compareTo}
      onCompareFromChange={compare.setCompareFrom}
      onCompareToChange={compare.setCompareTo}
      onCompareRangeChange={compare.setCompareRange}
      compareById={compareById}
      compareTotals={compareQuery.data?.totals ?? {}}
      onComparePreset={compare.applyPreset}
      granularity={compare.granularity}
      onGranularityChange={compare.setGranularity}
      countryKey={countryKey}
      wiserIds={wiserIds}
      tagFilterColors={tagColors}
      tagsLoading={perfTagsQuery.isLoading}
    />
  );
};

export default GatewayStatsTab;
